import { api } from "./api";
import {
  AttendanceSubject,
  EventAttendanceRoster,
  EventAttendanceSession,
  EventAttendanceSummary,
  EventAttendee,
} from "../../models/eventAttendance";

/**
 * Toma de asistencia por evento desde el BO, contra `/bo/events/:id/attendance*` (mismo motor
 * que el mobile, con admin-auth + tenant seleccionado). El header X-Tenant-Context lo adjunta
 * `api.ts`; el backend valida que el evento sea del tenant seleccionado.
 */

const rosterTag = (eventId: number) => ({ type: "eventAttendanceRoster" as const, id: eventId });

const unwrap = (response: unknown): Record<string, unknown> => {
  const record = (response ?? {}) as Record<string, unknown>;
  const data = record.success === true && record.data !== undefined ? record.data : record;
  return (data ?? {}) as Record<string, unknown>;
};

const toInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableInt = (value: unknown): number | null =>
  value === null || value === undefined ? null : toInt(value, 0);

const toStr = (value: unknown): string => (typeof value === "string" ? value : "");

const toNullableStr = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const toPresence = (value: unknown): boolean | null =>
  value === true ? true : value === false ? false : null;

const mapSession = (raw: unknown): EventAttendanceSession => {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    id: toInt(row.id, 0),
    status: row.status === "completed" ? "completed" : "in_progress",
    startedAt: toStr(row.started_at),
    completedAt: toNullableStr(row.completed_at),
  };
};

const mapSummary = (raw: unknown): EventAttendanceSummary => {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    total: toInt(row.total, 0),
    present: toInt(row.present, 0),
    absent: toInt(row.absent, 0),
    unmarked: toInt(row.unmarked, 0),
  };
};

const mapAttendee = (raw: unknown): EventAttendee => {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    id: toNullableInt(row.id),
    userId: toNullableInt(row.user_id),
    companionId: toNullableStr(row.companion_id),
    name: toStr(row.name),
    email: toNullableStr(row.email),
    avatarUrl: toNullableStr(row.avatar_url),
    isPresent: toPresence(row.is_present),
    isCompanion: row.is_companion === true,
    companionOf: toNullableInt(row.companion_of),
    markedAt: toNullableStr(row.marked_at),
    companionsCount: toInt(row.companions_count, 0),
    isHost: row.is_host === true,
  };
};

const mapRoster = (response: unknown): EventAttendanceRoster => {
  const data = unwrap(response);
  const attendees = Array.isArray(data.attendees) ? data.attendees : [];
  return {
    session: mapSession(data.session),
    summary: mapSummary(data.summary),
    attendees: attendees.map(mapAttendee),
  };
};

const subjectBody = (subject: AttendanceSubject): Record<string, unknown> =>
  subject.userId !== undefined
    ? { user_id: subject.userId }
    : { companion_id: subject.companionId };

export interface MarkAttendanceArgs {
  eventId: number;
  subject: AttendanceSubject;
  isPresent: boolean;
}

export interface UpdateAttendanceArgs {
  eventId: number;
  attendanceId: number;
  isPresent: boolean;
}

export const eventAttendanceBoApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBoEventAttendanceRoster: build.query<EventAttendanceRoster, number>({
      query: (eventId) => ({ url: `/bo/events/${eventId}/attendance` }),
      transformResponse: mapRoster,
      providesTags: (_result, _error, eventId) => [rosterTag(eventId)],
    }),

    markBoEventAttendance: build.mutation<EventAttendanceRoster, MarkAttendanceArgs>({
      query: ({ eventId, subject, isPresent }) => ({
        url: `/bo/events/${eventId}/attendance/mark`,
        method: "PATCH",
        body: { ...subjectBody(subject), is_present: isPresent },
      }),
      // La respuesta trae sólo la marca + summary; refetch del padrón para reflejar la fila.
      invalidatesTags: (_result, _error, { eventId }) => [rosterTag(eventId)],
    }),

    updateBoEventAttendance: build.mutation<EventAttendanceRoster, UpdateAttendanceArgs>({
      query: ({ eventId, attendanceId, isPresent }) => ({
        url: `/bo/events/${eventId}/attendance/${attendanceId}`,
        method: "PATCH",
        body: { is_present: isPresent },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [rosterTag(eventId)],
    }),

    markAllBoEventAttendance: build.mutation<EventAttendanceRoster, number>({
      query: (eventId) => ({
        url: `/bo/events/${eventId}/attendance/mark-all`,
        method: "POST",
      }),
      transformResponse: mapRoster,
      invalidatesTags: (_result, _error, eventId) => [rosterTag(eventId)],
    }),

    completeBoEventAttendance: build.mutation<
      { session: EventAttendanceSession; summary: EventAttendanceSummary },
      number
    >({
      query: (eventId) => ({
        url: `/bo/events/${eventId}/attendance/complete`,
        method: "POST",
      }),
      transformResponse: (response: unknown) => {
        const data = unwrap(response);
        return { session: mapSession(data.session), summary: mapSummary(data.summary) };
      },
      invalidatesTags: (_result, _error, eventId) => [rosterTag(eventId)],
    }),
  }),
});

export const {
  useGetBoEventAttendanceRosterQuery,
  useMarkBoEventAttendanceMutation,
  useUpdateBoEventAttendanceMutation,
  useMarkAllBoEventAttendanceMutation,
  useCompleteBoEventAttendanceMutation,
} = eventAttendanceBoApi;
